export const DEPOSIT_MERGE_FAMILY = "deposit-merge-v1" as const;

export interface DepositMergeProverConfig {
  url: string;
  sha256: string;
  byteLength: number;
  wasmUrl?: string;
}

export interface DepositMergeProofResponse {
  family: typeof DEPOSIT_MERGE_FAMILY;
  proof: string;
  publicInputs: string[];
  locallyVerified: true;
}

export interface DepositMergeProver {
  prove(
    request: Record<string, unknown>,
    signal?: AbortSignal,
  ): Promise<DepositMergeProofResponse>;
}

interface PreparedClient extends DepositMergeProver {
  prepare(): Promise<void>;
  dispose(): void;
}

type Standby = {
  key: string;
  ready: Promise<PreparedClient | null>;
  client?: PreparedClient;
  released: boolean;
  timer?: ReturnType<typeof setTimeout>;
};
let standby: Standby | undefined;
const configKey = (config: DepositMergeProverConfig) => JSON.stringify(config);

function fail(message: string): never {
  throw new Error(`Deposit merge rejected: ${message}`);
}

export function depositMergeProverConfigFromEnv(): DepositMergeProverConfig | null {
  const url = process.env.REACT_APP_DEPOSIT_MERGE_KEY_URL?.trim();
  const sha256 = process.env.REACT_APP_DEPOSIT_MERGE_KEY_SHA256?.trim();
  const bytes = process.env.REACT_APP_DEPOSIT_MERGE_KEY_BYTES?.trim();
  const wasmUrl = process.env.REACT_APP_DEPOSIT_MERGE_WASM_URL?.trim();
  if (!url && !sha256 && !bytes && !wasmUrl) return null;
  if (
    !url ||
    !sha256 ||
    !/^[a-f0-9]{64}$/.test(sha256) ||
    !bytes ||
    !/^[1-9][0-9]{0,8}$/.test(bytes)
  ) {
    fail("deposit merge proving artifact configuration is incomplete");
  }
  const byteLength = Number(bytes);
  if (
    !Number.isSafeInteger(byteLength) ||
    byteLength <= 0 ||
    byteLength > 32 * 1024 * 1024
  ) {
    fail("deposit merge proving artifact size is invalid");
  }
  return { url, sha256, byteLength, ...(wasmUrl ? { wasmUrl } : {}) };
}

/**
 * Load the optional browser prover only after an explicitly pinned artifact is
 * configured. The ordinary deposit bundle never needs a private witness or a
 * server prover fallback.
 */
async function loadClient(
  config: DepositMergeProverConfig,
): Promise<PreparedClient | null> {
  const imported =
    (await import("@stealthos/joinsplit-prover-wasm")) as unknown as {
      DepositMergeClient?: new (
        config: Record<string, unknown>,
      ) => PreparedClient;
      createDepositMergeProverWorker?: () => Worker;
    };
  if (
    !imported.DepositMergeClient ||
    !imported.createDepositMergeProverWorker
  ) {
    return null;
  }
  return new imported.DepositMergeClient({
    key: {
      url: config.url,
      sha256: config.sha256,
      byteLength: config.byteLength,
    },
    createWorker: imported.createDepositMergeProverWorker,
    ...(config.wasmUrl ? { wasmUrl: config.wasmUrl } : {}),
  });
}

/** Warm only a checked public key; no account, note, witness or signature is
 * accepted here. An unused worker has a bounded lifetime and form cleanup. */
export function warmDepositMergeProver(): () => void {
  let config: DepositMergeProverConfig | null;
  try {
    config = depositMergeProverConfigFromEnv();
  } catch {
    return () => {};
  }
  if (!config || typeof Worker === "undefined") return () => {};
  const key = configKey(config);
  if (standby?.key === key) return () => {};
  if (standby) {
    standby.released = true;
    clearTimeout(standby.timer);
    standby.client?.dispose();
  }
  const entry: Standby = { key, released: false, ready: Promise.resolve(null) };
  standby = entry;
  const release = () => {
    // Once claimed, the deposit's abort signal owns the single-use worker.
    if (standby !== entry) return;
    standby = undefined;
    entry.released = true;
    clearTimeout(entry.timer);
    entry.client?.dispose();
  };
  entry.ready = loadClient(config)
    .then(async (client) => {
      if (!client) return null;
      entry.client = client;
      if (entry.released) {
        client.dispose();
        return null;
      }
      await client.prepare();
      return entry.released ? null : client;
    })
    .catch(() => {
      release();
      return null; // Optional warmup failure cannot block the normal proving path.
    });
  entry.timer = setTimeout(release, 120_000);
  return release;
}

export async function createDepositMergeProverFromEnv(): Promise<DepositMergeProver | null> {
  const config = depositMergeProverConfigFromEnv();
  if (!config) return null;
  const cold = await loadClient(config);
  if (!cold) return null;
  // Capability checks may construct this wrapper without proving. Only an
  // actual proof can acquire the standby worker.
  return {
    async prove(request, signal) {
      if (signal?.aborted)
        throw new DOMException("Deposit cancelled", "AbortError");
      const candidate =
        standby?.key === configKey(config) ? standby : undefined;
      if (candidate) {
        standby = undefined;
        clearTimeout(candidate.timer);
      }
      const abortPreparation = () => {
        if (!candidate) return;
        candidate.released = true;
        candidate.client?.dispose();
      };
      signal?.addEventListener("abort", abortPreparation, { once: true });
      try {
        const client = (await candidate?.ready) ?? cold;
        if (signal?.aborted)
          throw new DOMException("Deposit cancelled", "AbortError");
        return await client.prove(request, signal);
      } finally {
        signal?.removeEventListener("abort", abortPreparation);
      }
    },
  };
}
